import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useSiteSettings } from '../App';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AuthPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const { siteSettings } = useSiteSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const loginSoundRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings/login-sound`)
      .then((r) => {
        if (r.data.enabled !== false && r.data.sound_url) {
          loginSoundRef.current = r.data.sound_url;
        }
      })
      .catch(() => {});
  }, []);

  const playLoginSound = (token, user) => {
    const soundUrl = loginSoundRef.current;
    axios.get(`${API}/me/login-sound-pref`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data.enabled === false) return;
        let announceDelay = 0;
        if (soundUrl) {
          const url = soundUrl.startsWith('/api/') ? `${BACKEND_URL}${soundUrl}` : soundUrl;
          const audio = new Audio(url);
          audio.volume = 0.7;
          audio.play().catch(() => {});
          announceDelay = 1400;
        }
        if (user?.role === 'employee' && 'speechSynthesis' in window) {
          const firstName = (user.name || '').trim().split(' ')[0];
          if (firstName) {
            setTimeout(() => {
              const utterance = new SpeechSynthesisUtterance(`Welcome, ${firstName}`);
              utterance.rate = 0.95;
              window.speechSynthesis.speak(utterance);
            }, announceDelay);
          }
        }
      })
      .catch(() => {});
  };
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
    phone: '',
    address: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Unlock speech synthesis on iOS/Safari while still inside the user gesture
    if ('speechSynthesis' in window) {
      try { window.speechSynthesis.speak(new SpeechSynthesisUtterance('')); } catch (err) { /* noop */ }
    }

    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      toast.success('Login successful!');
      playLoginSound(response.data.access_token, response.data.user);
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      
      // Navigate to pending approval page with registration data
      navigate('/pending-approval', {
        state: {
          email: registerForm.email,
          name: registerForm.name,
          role: registerForm.role
        }
      });
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success('Password reset link sent! Check your email.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: siteSettings.login_background 
          ? `url(${BACKEND_URL}${siteSettings.login_background})`
          : 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8 fade-in">
          <div className="text-center mb-8">
            {siteSettings.site_logo ? (
              <>
                <div className="mb-4 p-6 bg-white rounded-xl inline-flex items-center justify-center shadow-lg min-w-[300px] min-h-[140px]">
                  <img 
                    src={`${BACKEND_URL}${siteSettings.site_logo}`} 
                    alt={siteSettings.site_title}
                    className="max-h-32 max-w-[280px] object-contain"
                    onError={(e) => { console.error('Logo failed to load:', e); e.target.style.border = '2px solid red'; }}
                  />
                </div>
                {siteSettings.site_title && siteSettings.site_title !== 'BuildTrack' && (
                  <h2 className="text-2xl font-bold text-white mb-2">{siteSettings.site_title}</h2>
                )}
              </>
            ) : (
              siteSettings.site_title && siteSettings.site_title !== 'BuildTrack' && (
                <h1 className="text-4xl font-bold text-white mb-2">{siteSettings.site_title}</h1>
              )
            )}
            {siteSettings.site_subtitle && siteSettings.site_subtitle !== 'Construction Project Management' && (
              <p className="text-slate-400">{siteSettings.site_subtitle}</p>
            )}
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {!showForgotPassword ? (
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <PasswordInput
                      id="login-password"
                      data-testid="login-password-input"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-semibold text-white mb-2">Reset Password</h3>
                    <p className="text-slate-400 text-sm">Enter your email to receive a password reset link</p>
                  </div>
                  <div>
                    <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="your@email.com"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1 bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
                      disabled={isLoading}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div>
                  <Label htmlFor="register-name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="register-name"
                    data-testid="register-name-input"
                    type="text"
                    placeholder="John Doe"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-slate-300">Email</Label>
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-slate-300">Password</Label>
                  <PasswordInput
                    id="register-password"
                    data-testid="register-password-input"
                    placeholder="••••••••"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-role" className="text-slate-300">Account Type</Label>
                  <select
                    id="register-role"
                    value={registerForm.role}
                    onChange={(e) => setRegisterForm({ ...registerForm, role: e.target.value })}
                    className="mt-1 w-full bg-white border border-gray-200 rounded-md px-3 py-2 text-gray-900 min-h-[44px]"
                    data-testid="register-role-select"
                  >
                    <option value="client">Client Account</option>
                    <option value="sub_contractor">Sub Contractor</option>
                  </select>
                  <p className="text-xs text-slate-400 mt-1">New accounts require admin approval before access</p>
                </div>
                <div>
                  <Label htmlFor="register-phone" className="text-slate-300">Phone Number</Label>
                  <Input
                    id="register-phone"
                    data-testid="register-phone-input"
                    type="tel"
                    placeholder="+1234567890"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-address" className="text-slate-300">Address</Label>
                  <Input
                    id="register-address"
                    data-testid="register-address-input"
                    type="text"
                    placeholder="123 Main St, City"
                    value={registerForm.address}
                    onChange={(e) => setRegisterForm({ ...registerForm, address: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg shadow-md transition-all min-h-[48px]"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating account...' : 'Register'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;